import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { CollaborateurDTO } from '@logaia_users/logaia-dto';
import { DossierContext } from '@shared/models/dossier-extended-dto.model';
import { CollaborateurService } from '@shared/services/cabinet/collaborateur.service';
import { DossierService } from '@shared/services/cabinet/dossier.service';
import { PreferenceService } from '@shared/services/cabinet/preference.service';
import { DossierResolverService } from '@shared/services/dossier-resolver.service';
import { DossierUtil } from '@shared/utils/dossier.util';
import { forkJoin, map, of } from 'rxjs';

const SIGNIN_PATH = '/signin';

export const dossierResolver: ResolveFn<DossierContext> = (
  route: ActivatedRouteSnapshot,
) => {
  const dossierService = inject(DossierService);
  const prefService = inject(PreferenceService);
  if (prefService.isClient) {
    // Si on recharge la page, il faut récupérer le cabinetId avant DossierResolverService.resolve().
    // Une fois lancer, le cabinetId sera modifié à chaque récupération de dossier.
    const cabinetId = dossierService.cabinetId;
    // Si la connexion vient d'avoir lieu, les dossiers viennent d'être charger.
    const router = inject(Router);
    const navigation = router.currentNavigation();
    const from = navigation?.extras.state?.['from'];

    let dossiers$ = of(inject(DossierService).dossiers);
    if (from === SIGNIN_PATH) {
      if (navigation && navigation.extras.state) {
        navigation.extras['state']['from'] = undefined;
      }
    } else if (inject(DossierService).dossiers.length === 0) {
      dossiers$ = inject(DossierResolverService).resolve();
    }

    return dossiers$.pipe(
      map((dossiers) => {
        const resolvedDossier = dossiers.find(
          (d) =>
            d.id === +route.paramMap.get('id')! &&
            (cabinetId === 0 || d.cabinetId === cabinetId),
        );
        if (!resolvedDossier) {
          throw new Error(
            `Dossier ${route.paramMap.get('id')}, cabinet: ${cabinetId} introuvable.`,
          );
        }

        dossierService.changeDossier(resolvedDossier);
        dossierService.storeCabinetId(cabinetId);
        const preferences = resolvedDossier.interlocuteurs.find(
          (i) => i.login === prefService.user.login,
        )?.preferences;
        prefService.setCollaborateur({
          preferences,
        } as CollaborateurDTO);
        return resolvedDossier;
      }),
    );
  }

  return forkJoin([
    dossierService.get(+route.paramMap.get('id')!),
    dossierService.getTravail(+route.paramMap.get('id')!),
    dossierService.getSituation(+route.paramMap.get('id')!),
  ]).pipe(
    map(([dossier, travail, situation]) => {
      const dossierContext = {
        ...dossier,
        travail,
        situation,
        isSaisieTravailAutorise: DossierUtil.isSaisieTravailAutorise(
          travail,
          prefService.user.login,
        ),
      } as DossierContext;
      dossierService.dossiers = [dossierContext];
      dossierService.changeDossier(dossierContext);
      return dossierContext;
    }),
  );
};
